// ============================================================
// AtencionFormModal — crear / editar una atención psicológica.
// ============================================================

import React, { useState, useEffect } from 'react';
import { I } from 'components/ui';
import BuscadorMatricula from './BuscadorMatricula';
import { atencionPsicologicaService } from 'services/atencionPsicologicaService';
import { matriculaService } from 'services/matriculaService';
import {
  AtencionPsicologicaPayload,
  AtencionPsicologicaResponse,
  TipoAtencion,
  MotivoCategoria,
  EstadoAtencion,
  TIPO_ATENCION_LABEL,
  MOTIVO_CATEGORIA_LABEL,
  ESTADO_ATENCION_LABEL,
} from 'types/psicologia.types';
import { MatriculaResponse } from 'types/matricula.types';

interface Props {
  initial?: AtencionPsicologicaResponse | null;        // edit mode si viene
  matriculaPreseleccionada?: MatriculaResponse | null; // crear con un estudiante ya elegido
  fechaSugerida?: string;                              // YYYY-MM-DD para inicializar fecha
  onClose: () => void;
  onSaved: (a: AtencionPsicologicaResponse) => void;
}

const TIPOS:    TipoAtencion[]    = ['ESTUDIANTE', 'FAMILIAR', 'MIXTA'];
const MOTIVOS:  MotivoCategoria[] = ['ANSIEDAD','DEPRESION','BULLYING','FAMILIAR','ACADEMICO','CONDUCTA','DUELO','ORIENTACION_VOCACIONAL','OTRO'];
const ESTADOS:  EstadoAtencion[]  = ['PROGRAMADA', 'REALIZADA', 'CANCELADA'];

const Spinner: React.FC = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const AtencionFormModal: React.FC<Props> = ({
  initial, matriculaPreseleccionada, fechaSugerida, onClose, onSaved,
}) => {
  const isEdit = !!initial;

  const [matricula, setMatricula] = useState<MatriculaResponse | null>(matriculaPreseleccionada ?? null);
  const [fecha, setFecha] = useState(fechaSugerida ?? new Date().toISOString().slice(0, 10));
  const [hora, setHora] = useState('09:00');
  const [duracionMin, setDuracionMin] = useState(45);
  const [tipo, setTipo] = useState<TipoAtencion>('ESTUDIANTE');
  const [motivoCategoria, setMotivoCategoria] = useState<MotivoCategoria>('ANSIEDAD');
  const [motivoDescripcion, setMotivoDescripcion] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [resumenParaFamilia, setResumenParaFamilia] = useState('');
  const [compartirConFamilia, setCompartirConFamilia] = useState(false);
  const [estado, setEstado] = useState<EstadoAtencion>('PROGRAMADA');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hidratar formulario en modo edición
  useEffect(() => {
    if (!initial) return;
    const dt = new Date(initial.fechaHora);
    setFecha(initial.fechaHora.slice(0, 10));
    setHora(initial.fechaHora.slice(11, 16) || `${dt.getHours().toString().padStart(2,'0')}:${dt.getMinutes().toString().padStart(2,'0')}`);
    setDuracionMin(initial.duracionMin);
    setTipo(initial.tipo);
    if (initial.motivoCategoria) setMotivoCategoria(initial.motivoCategoria);
    setMotivoDescripcion(initial.motivoDescripcion ?? '');
    setObservaciones(initial.observaciones ?? '');
    setResumenParaFamilia(initial.resumenParaFamilia ?? '');
    setCompartirConFamilia(!!initial.compartirConFamilia);
    setEstado(initial.estado);
    // Construir un pseudo-matricula con los datos que vienen embebidos
    setMatricula({
      idMatricula:           initial.matriculaId,
      codigoMatricula:       null,
      fase:                  'REGULAR',
      estudianteId:          initial.estudianteId,
      estudianteNombres:     initial.estudianteNombres,
      estudianteApellidos:   initial.estudianteApellidos,
      estudianteDni:         initial.estudianteDni,
      estudianteCodigoSiagie:null,
      gradoId:               0, gradoNombre: initial.gradoNombre, gradoNivel: initial.nivel,
      seccionId:             0, seccionNombre: initial.seccionNombre,
      anioEscolarId:         0, anio: 0,
      fechaMatricula:        null, activo: true,
      pagoMatricula:         null, pensiones: null,
    });
  }, [initial]);

  const enviar = async () => {
    if (!matricula) { setError('Selecciona un estudiante.'); return; }
    if (!fecha || !hora) { setError('Indica fecha y hora.'); return; }
    if (duracionMin <= 0) { setError('La duración debe ser mayor a 0.'); return; }

    const payload: AtencionPsicologicaPayload = {
      matriculaId:         matricula.idMatricula,
      fechaHora:           `${fecha}T${hora}:00`,
      duracionMin,
      tipo,
      motivoCategoria,
      motivoDescripcion:   motivoDescripcion.trim() || undefined,
      observaciones:       observaciones.trim() || undefined,
      resumenParaFamilia:  resumenParaFamilia.trim() || undefined,
      compartirConFamilia,
      estado:              isEdit ? estado : 'PROGRAMADA',
    };

    setSubmitting(true);
    setError(null);
    try {
      const saved = isEdit
        ? await atencionPsicologicaService.actualizar(initial!.idAtencionPsicologica, payload)
        : await atencionPsicologicaService.crear(payload);
      onSaved(saved);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al guardar la atención');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-head">
          <div>
            <h2>{isEdit ? 'Editar atención' : 'Nueva atención'}</h2>
            <div className="sub">{isEdit ? 'Modifica los detalles de la sesión' : 'Programa una sesión psicológica'}</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>

        <div className="modal-body grid gap-4">

          {/* Estudiante */}
          <div className="field">
            <label>Estudiante <span className="required">*</span></label>
            <BuscadorMatricula
              selected={matricula}
              onSelected={setMatricula}
              onClear={isEdit ? undefined : () => setMatricula(null)}
            />
          </div>

          {/* Fecha + hora + duración */}
          <div className="grid grid-cols-3 gap-3">
            <div className="field">
              <label>Fecha <span className="required">*</span></label>
              <input type="date" className="input" value={fecha} onChange={e => setFecha(e.target.value)} />
            </div>
            <div className="field">
              <label>Hora <span className="required">*</span></label>
              <input type="time" className="input" value={hora} onChange={e => setHora(e.target.value)} />
            </div>
            <div className="field">
              <label>Duración (min)</label>
              <input
                type="number"
                min={5}
                max={240}
                step={5}
                className="input"
                value={duracionMin}
                onChange={e => setDuracionMin(Number(e.target.value) || 45)}
              />
            </div>
          </div>

          {/* Tipo + categoría */}
          <div className="grid grid-cols-2 gap-3">
            <div className="field">
              <label>Tipo de sesión</label>
              <select className="input" value={tipo} onChange={e => setTipo(e.target.value as TipoAtencion)}>
                {TIPOS.map(t => <option key={t} value={t}>{TIPO_ATENCION_LABEL[t]}</option>)}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">
                {tipo === 'ESTUDIANTE' && 'Solo el estudiante asistirá.'}
                {tipo === 'FAMILIAR'   && 'Solo la familia (sin estudiante).'}
                {tipo === 'MIXTA'      && 'Estudiante + familia.'}
              </p>
            </div>
            <div className="field">
              <label>Motivo (categoría)</label>
              <select className="input" value={motivoCategoria} onChange={e => setMotivoCategoria(e.target.value as MotivoCategoria)}>
                {MOTIVOS.map(m => <option key={m} value={m}>{MOTIVO_CATEGORIA_LABEL[m]}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <label>Motivo (descripción breve)</label>
            <input
              className="input"
              value={motivoDescripcion}
              onChange={e => setMotivoDescripcion(e.target.value)}
              placeholder="Episodio de pánico en clase…"
              maxLength={500}
            />
          </div>

          {/* Notas internas — destacadas en rojo/candado */}
          <div className="border border-red-200 bg-red-50/60 rounded-xl p-3">
            <label className="flex items-center gap-1.5 text-[11px] font-bold text-red-700 uppercase tracking-wide mb-1">
              <I.Lock size={12} /> Notas internas (confidenciales)
            </label>
            <textarea
              className="input"
              rows={4}
              value={observaciones}
              onChange={e => setObservaciones(e.target.value)}
              placeholder="Solo tú y dirección verán esto…"
            />
            <p className="text-[11px] text-red-600 mt-1">
              Estas notas nunca se muestran al estudiante ni a la familia.
            </p>
          </div>

          {/* Resumen para familia */}
          <div className="border border-blue-200 bg-blue-50/60 rounded-xl p-3">
            <label className="flex items-center gap-1.5 text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1">
              <I.Users size={12} /> Resumen para la familia
            </label>
            <textarea
              className="input"
              rows={3}
              value={resumenParaFamilia}
              onChange={e => setResumenParaFamilia(e.target.value)}
              placeholder="Versión apta para padres (opcional)…"
            />
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={compartirConFamilia}
                onChange={e => setCompartirConFamilia(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-semibold text-slate-700">
                Compartir este resumen con la familia
              </span>
            </label>
          </div>

          {/* Estado (solo en edición) */}
          {isEdit && (
            <div className="field">
              <label>Estado</label>
              <select className="input" value={estado} onChange={e => setEstado(e.target.value as EstadoAtencion)}>
                {ESTADOS.map(es => <option key={es} value={es}>{ESTADO_ATENCION_LABEL[es]}</option>)}
              </select>
            </div>
          )}

          {error && (
            <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={enviar} disabled={submitting}>
            {submitting ? <Spinner /> : null}
            {isEdit ? 'Guardar cambios' : 'Programar sesión'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AtencionFormModal;
